import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { Box, Group } from '@mantine/core'
import { AppButton, AppText } from '../../../app/common/ui'
import { format } from 'date-fns'
import DOMPurify from 'dompurify'
import LoadingComponent from '../../../app/layout/LoadingComponents'
import Breadcrumb from '../../../app/common/Breadcrumb'
import PartsCornerOverlay from '../../../app/common/PartsCornerOverlay'
import AdminToolsPane from './AdminToolsPane'
import { useStore } from '../../../app/stores/store'

function sanitizeHtml (html: string): string {
  if (!html) return ''
  const hasHtml = /<[a-z][\s\S]*>/i.test(html)
  const content = hasHtml ? html : html.replace(/\n/g, '<br />')
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote', 'a', 'span'],
    ALLOWED_ATTR: ['href', 'class', 'style']
  })
}

function StarRating ({ value }: { value: number }) {
  return (
    <Box component="span" aria-label={`${value} out of 5 stars`} sx={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          component="span"
          sx={{ color: star <= value ? 'var(--gs-gold)' : 'var(--gs-border)', fontSize: 18 }}
        >
          ★
        </Box>
      ))}
    </Box>
  )
}

export default observer(function FirearmDetails () {
  const { firearmStore, userStore } = useStore()
  const { selectedFirearm: firearm, loadFirearm, loadingInitial, deleteFirearm, loading } = firearmStore
  const { id } = useParams<{ id: string }>()
  const history = useHistory()

  useEffect(() => {
    if (id) loadFirearm(id)
    return () => firearmStore.clearSelectedFirearm()
  }, [id, loadFirearm, firearmStore])

  const handleDelete = () => {
    if (!firearm || !id) return
    if (window.confirm('Delete this firearm entry?')) {
      deleteFirearm(id).then(() => history.push('/gun-vault'))
    }
  }

  const isAuthor = userStore.user?.username === firearm?.authorUsername
  const allImages = firearm?.images ?? []
  const defaultImage = allImages.find((i) => i.isMain)?.url || allImages[0]?.url || firearm?.coverImageUrl
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const displayImage = selectedImageUrl ?? defaultImage

  if (loadingInitial || !firearm) {
    return <LoadingComponent content="Loading..." />
  }

  const breadcrumbItems = [
    { label: 'Gun Vault', href: '/gun-vault' },
    { label: `${firearm.manufacturer} ${firearm.model}` }
  ]

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
      <Box mb="md" sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <AppText
          component={Link}
          to="/gun-vault"
          size="sm"
          sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
        >
          ← Back to Gun Vault
        </AppText>
        <Breadcrumb items={breadcrumbItems} />
      </Box>

      <Box
        sx={{
          background: 'var(--gs-surface)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          padding: 24
        }}
      >
        <Box mb="md">
          <Group spacing="sm" mb={4}>
            <AppText size="xs" color="dimmed">
              {format(new Date(firearm.createdAt), 'MMMM d, yyyy')}
            </AppText>
            <AppText size="xs" color="dimmed">
              by{' '}
              <AppText
                component={Link}
                to={`/profiles/${firearm.authorUsername}`}
                size="xs"
                sx={{ color: 'var(--gs-accent)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {firearm.authorDisplayName || firearm.authorUsername}
              </AppText>
            </AppText>
          </Group>
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, color: 'var(--gs-text)' }}>
            {firearm.manufacturer} {firearm.model}
          </AppText>
          {firearm.countryOfOrigin && (
            <AppText size="sm" color="dimmed" mt={2}>{firearm.countryOfOrigin}</AppText>
          )}
          <Box mt="xs">
            <StarRating value={firearm.rating} />
          </Box>
        </Box>

        {displayImage && (
          <Box sx={{ position: 'relative', marginBottom: 16 }}>
            <Box
              sx={{
                borderRadius: 8,
                overflow: 'hidden',
                maxHeight: 400
              }}
            >
              <img src={displayImage} alt="" style={{ width: '100%', objectFit: 'cover' }} />
            </Box>
            <PartsCornerOverlay parts={firearm.parts ?? []} />
          </Box>
        )}

        {allImages.length > 1 && (
          <Box sx={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {allImages.map((img) => (
              <Box
                key={img.id}
                component="button"
                type="button"
                onClick={() => setSelectedImageUrl(img.url)}
                title="View full size"
                sx={{
                  position: 'relative',
                  width: 72,
                  height: 52,
                  padding: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: displayImage === img.url ? '2px solid var(--gs-accent)' : '2px solid var(--gs-border)',
                  cursor: 'pointer',
                  background: 'none',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {img.isMain && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      right: 2,
                      background: 'var(--gs-accent)',
                      color: '#fff',
                      fontSize: 8,
                      fontWeight: 700,
                      padding: '1px 4px',
                      borderRadius: 2
                    }}
                  >
                    Main
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {firearm.thoughtsAndOpinions && (
          <Box mb="md">
            <AppText weight={600} size="sm" mb="xs" sx={{ color: 'var(--gs-text)', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
              Thoughts &amp; Opinions
            </AppText>
            <Box
              sx={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gs-text)' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(firearm.thoughtsAndOpinions) }}
            />
          </Box>
        )}

        {firearm.review && (
          <Box mb="md">
            <AppText weight={600} size="sm" mb="xs" sx={{ color: 'var(--gs-text)', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
              Review
            </AppText>
            <Box
              sx={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gs-text)' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(firearm.review) }}
            />
          </Box>
        )}

        {firearm.notableThoughts && (
          <Box mb="md">
            <AppText weight={600} size="sm" mb="xs" sx={{ color: 'var(--gs-text)', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>
              Notable Thoughts
            </AppText>
            <Box
              sx={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gs-text)' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(firearm.notableThoughts) }}
            />
          </Box>
        )}

        {isAuthor && (
          <Group spacing="xs" mt="xl" sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 16 }}>
            <AppButton
              component={Link}
              to={`/manageFirearm/${firearm.id}`}
              size="sm"
              variant="default"
              sx={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Edit
            </AppButton>
            <AppButton
              size="sm"
              color="red"
              variant="subtle"
              loading={loading}
              onClick={handleDelete}
            >
              {loading ? 'Deleting…' : 'Delete'}
            </AppButton>
          </Group>
        )}

        <AdminToolsPane firearm={firearm} onFirearmDeleted={() => history.push('/gun-vault')} />
      </Box>
    </Box>
  )
})
